/**
 * Content Queue Page
 * Lists all content with filtering. Adapted: Link from react-router-dom.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  DataTable,
  StatusBadge,
  DateRangePicker,
  EmptyState,
  type Column,
  type DateRange,
} from "@/components/shared";
import { trpc } from "@/lib/trpc";

interface ContentItem {
  id: string;
  title: string;
  platforms: string[];
  status:
    | "draft"
    | "pending_review"
    | "approved"
    | "scheduled"
    | "published"
    | "rejected"
    | "failed";
  scheduled_for: string | null;
  created_at: string;
}

export function ContentPage() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  const {
    data: postsData,
    isLoading,
    error,
    refetch,
  } = trpc.posts.list.useQuery({
    limit: 20,
    status: statusFilter !== "all" ? (statusFilter as any) : undefined,
    platform: platformFilter !== "all" ? (platformFilter as any) : undefined,
  });
  const posts = postsData?.posts || [];
  const filteredContent = posts.filter((item) => {
    const createdDate = new Date(item.created_at);
    return createdDate >= dateRange.start && createdDate <= dateRange.end;
  });

  const columns: Column<ContentItem>[] = [
    {
      key: "title",
      label: "Title",
      sortable: true,
      render: (item) => (
        <Link
          to={`/content/${item.id}`}
          className="font-medium text-primary-400 hover:text-primary-300 hover:underline"
        >
          {item.title}
        </Link>
      ),
    },
    {
      key: "platforms",
      label: "Platforms",
      render: (item) => (
        <div className="flex flex-wrap gap-1">
          {item.platforms.map((p) => (
            <span
              key={p}
              className="inline-flex items-center rounded-md bg-bg-elevated px-2 py-1 text-xs font-medium text-text-secondary border border-border-subtle capitalize"
            >
              {p}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (item) => <StatusBadge status={item.status} />,
    },
    {
      key: "scheduled_for",
      label: "Scheduled Date",
      sortable: true,
      render: (item) =>
        item.scheduled_for ? (
          <time dateTime={item.scheduled_for}>
            {new Date(item.scheduled_for).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </time>
        ) : (
          <span className="text-text-tertiary">-</span>
        ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <div className="flex gap-2">
          <Link
            to={`/content/${item.id}`}
            className="text-sm font-medium text-primary-400 hover:text-primary-300"
          >
            View
          </Link>
          {item.status === "draft" && (
            <Link
              to={`/content/${item.id}/edit`}
              className="text-sm font-medium text-text-secondary hover:text-text-primary"
            >
              Edit
            </Link>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              Content Queue
            </h1>
            <p className="mt-2 text-sm text-text-secondary">
              Manage and schedule your content across all platforms
            </p>
          </div>
          <Link
            to="/content/new"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 transition-colors"
          >
            + New Post
          </Link>
        </div>

        <div className="mb-6 rounded-lg border border-border-default bg-bg-secondary p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label
                htmlFor="status-filter"
                className="block text-sm font-medium text-text-secondary mb-2"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-md border border-border-default bg-bg-tertiary px-3 py-2 text-sm text-text-primary"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_review">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label
                htmlFor="platform-filter"
                className="block text-sm font-medium text-text-secondary mb-2"
              >
                Platform
              </label>
              <select
                id="platform-filter"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="w-full rounded-md border border-border-default bg-bg-tertiary px-3 py-2 text-sm text-text-primary"
              >
                <option value="all">All Platforms</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="reddit">Reddit</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="flex-1 min-w-[300px]">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Date Range
              </label>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/50 bg-red-950/20 p-4">
            <h3 className="text-sm font-medium text-red-400">
              Failed to load content
            </h3>
            <p className="text-sm text-red-300/80">{error.message}</p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-sm text-red-400 hover:bg-red-950/30 px-3 py-1.5 rounded-md"
            >
              Retry
            </button>
          </div>
        )}
        {isLoading && (
          <div className="rounded-lg border border-border-default bg-bg-secondary overflow-hidden">
            <div className="border-b border-border-default bg-bg-tertiary px-6 py-3">
              <div className="flex gap-4">
                {[20, 24, 16, 28, 16].map((w, i) => (
                  <div
                    key={i}
                    className="h-4 bg-bg-elevated rounded animate-pulse"
                    style={{ width: `${w * 4}px` }}
                  />
                ))}
              </div>
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border-b border-border-subtle px-6 py-4">
                <div className="flex gap-4 items-center">
                  <div className="h-4 bg-bg-elevated rounded animate-pulse w-1/4" />
                  <div className="h-6 bg-bg-elevated rounded animate-pulse w-32" />
                  <div className="h-6 bg-bg-elevated rounded animate-pulse w-20" />
                  <div className="h-4 bg-bg-elevated rounded animate-pulse w-24" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!isLoading && !error && filteredContent.length > 0 && (
          <DataTable
            columns={columns}
            data={filteredContent}
            emptyMessage="No content found matching your filters"
          />
        )}
        {!isLoading &&
          !error &&
          filteredContent.length === 0 &&
          posts.length === 0 && (
            <EmptyState
              title="No content yet"
              description="Get started by creating your first post."
              actionLabel="Create First Post"
              onAction={() => (window.location.href = "/content/new")}
              icon="pencil"
            />
          )}
        {!isLoading &&
          !error &&
          filteredContent.length === 0 &&
          posts.length > 0 && (
            <div className="rounded-lg border border-border-default bg-bg-secondary p-12 text-center">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                No matching content
              </h3>
              <button
                onClick={() => {
                  setStatusFilter("all");
                  setPlatformFilter("all");
                }}
                className="text-sm text-primary-400 hover:text-primary-300"
              >
                Clear Filters
              </button>
            </div>
          )}
      </div>
    </div>
  );
}
